import AddRounded from '@mui/icons-material/AddRounded';
import BlockRounded from '@mui/icons-material/BlockRounded';
import KeyRounded from '@mui/icons-material/KeyRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import { Box, Button, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState, QueryError } from '../../components/states';
import { StatusChip } from '../../components/StatusChip';
import { Segmented } from '../../components/ui';
import { date, dateTime, plural } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { tenantKeys } from '../tenants/api';
import { ReasonDialog } from '../tenants/parts';
import {
  channelLabel,
  CODE_STATUS_OPTIONS,
  codeKeys,
  createCode,
  deliverCode,
  fetchCodes,
  maskedCode,
  revokeCode,
  type ActivationCode,
  type CodeListQuery,
  type CodeStatus,
  type CreatedCode,
} from './api';
import { CodeRevealDialog, CreateCodeDialog, DeliverDialog } from './CodeDialogs';

export interface CodesState {
  status: CodeStatus | 'ALL';
  page: number;
  pageSize: number;
}

const srOnly = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  width: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
} as const;

const STATUS_LABEL: Record<CodeStatus, string> = {
  ACTIVE: 'Unused',
  REDEEMED: 'Redeemed',
  REVOKED: 'Revoked',
  EXPIRED: 'Expired',
};

/**
 * Activation codes table with status filter and row actions (mark delivered, revoke). With
 * `tenant`, it lists that shop's codes and can generate new ones (shown once).
 */
export function CodesPanel({
  tenant,
  tenantId,
  tenantName,
  state,
  onStateChange,
  toolbarExtra,
}: {
  /** Scope to one shop (tenant detail tab): enables "New code". */
  tenant?: { id: string; name: string };
  /** Global page: optional tenant filter. */
  tenantId?: string;
  /** Global page: shop name for a tenant id (codes carry only tenantId). */
  tenantName?: (id: string) => string | undefined;
  state: CodesState;
  onStateChange: (next: Partial<CodesState>) => void;
  toolbarExtra?: ReactNode;
}) {
  const { can } = useAuth();
  const manage = can('codes.manage');
  const notify = useNotify();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedCode | null>(null);
  const [revoking, setRevoking] = useState<ActivationCode | null>(null);
  const [delivering, setDelivering] = useState<ActivationCode | null>(null);

  const query: CodeListQuery = {
    page: state.page,
    pageSize: state.pageSize,
    ...(state.status === 'ALL' ? {} : { status: state.status }),
    ...(tenantId && !tenant ? { tenantId } : {}),
  };
  const list = useQuery({
    queryKey: [...codeKeys.list(query), tenant?.id ?? 'all'],
    queryFn: () => fetchCodes(query, tenant?.id),
    placeholderData: keepPreviousData,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: codeKeys.all });

  const columns: Column<ActivationCode>[] = [
    {
      key: 'code',
      header: 'Code',
      render: (c) => (
        <Box>
          <Typography
            variant="body2"
            className="tabular"
            sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 600 }}
            noWrap
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {maskedCode(c.codeHint)}
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              ••••{c.codeHint}
            </Box>
          </Typography>
          {!tenant && (
            <Typography
              variant="caption"
              component="div"
              noWrap
              sx={{ display: { xs: 'block', sm: 'none' }, fontWeight: 600, color: 'primary.main' }}
            >
              {tenantName?.(c.tenantId) ?? `${c.tenantId.slice(0, 8)}…`}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" component="div" noWrap>
            Created {date(c.createdAt)}
          </Typography>
        </Box>
      ),
    },
    ...(tenant
      ? []
      : [
          {
            key: 'tenant',
            header: 'Shop',
            hideBelow: 'sm',
            render: (c: ActivationCode) => (
              <Link
                component={RouterLink}
                to={`/tenants/${c.tenantId}`}
                underline="hover"
                sx={{ fontWeight: 600 }}
                onClick={(e) => e.stopPropagation()}
              >
                {tenantName?.(c.tenantId) ?? `${c.tenantId.slice(0, 8)}…`}
              </Link>
            ),
          } satisfies Column<ActivationCode>,
        ]),
    {
      key: 'status',
      header: 'Status',
      render: (c) => <StatusChip status={c.status} label={STATUS_LABEL[c.status]} />,
    },
    {
      key: 'days',
      header: 'Adds',
      align: 'right',
      hideBelow: 'sm',
      render: (c) => <span className="tabular">{plural(c.durationDays, 'day')}</span>,
    },
    {
      key: 'expires',
      header: 'Expires / used',
      hideBelow: 'md',
      render: (c) => (
        <span className="tabular" style={{ whiteSpace: 'nowrap' }}>
          {c.status === 'REDEEMED'
            ? `Redeemed ${dateTime(c.redeemedAt)}`
            : c.status === 'REVOKED'
              ? `Revoked ${dateTime(c.revokedAt)}`
              : date(c.expiresAt)}
        </span>
      ),
    },
    {
      key: 'delivered',
      header: 'Delivered',
      hideBelow: 'lg',
      render: (c) =>
        c.deliveredAt ? (
          <Box>
            {channelLabel(c.deliveredVia)}
            <Typography variant="caption" color="text.secondary" component="div">
              {dateTime(c.deliveredAt)}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" component="span">
            Not yet
          </Typography>
        ),
    },
    {
      key: 'notes',
      header: 'Notes',
      hideBelow: 'lg',
      render: (c) => (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 220 }} noWrap>
          {c.notes ?? '—'}
        </Typography>
      ),
    },
    ...(manage
      ? [
          {
            key: 'actions',
            header: (
              <Box component="span" sx={srOnly}>
                Actions
              </Box>
            ),
            align: 'right',
            width: 96,
            render: (c: ActivationCode) =>
              c.status === 'ACTIVE' ? (
                <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                  <Tooltip title="Mark delivered">
                    <IconButton
                      size="small"
                      aria-label={`Mark code ${c.codeHint} delivered`}
                      onClick={() => setDelivering(c)}
                    >
                      <SendRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Revoke">
                    <IconButton
                      size="small"
                      color="error"
                      aria-label={`Revoke code ${c.codeHint}`}
                      onClick={() => setRevoking(c)}
                    >
                      <BlockRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ) : null,
          } satisfies Column<ActivationCode>,
        ]
      : []),
  ];

  const filtered = state.status !== 'ALL' || Boolean(tenantId);

  return (
    <>
      {list.isError && <QueryError error={list.error} onRetry={() => void list.refetch()} />}
      <DataTable
        columns={columns}
        rows={list.data?.data}
        rowKey={(c) => c.id}
        loading={list.isPending}
        page={list.data?.page}
        onPageChange={(page) => onStateChange({ page })}
        onPageSizeChange={(pageSize) => onStateChange({ pageSize, page: 1 })}
        toolbar={
          <>
            <Box sx={{ flexShrink: 0 }}>
              <Segmented
                label="Code status"
                value={state.status}
                options={CODE_STATUS_OPTIONS}
                onChange={(status) => onStateChange({ status, page: 1 })}
              />
            </Box>
            {toolbarExtra}
            {tenant && manage && (
              <Button
                variant="contained"
                startIcon={<AddRounded />}
                onClick={() => setCreating(true)}
                sx={{ ml: { sm: 'auto !important' } }}
              >
                New code
              </Button>
            )}
          </>
        }
        empty={
          <EmptyState
            dense
            icon={<KeyRounded />}
            title={filtered ? 'No codes match' : 'No activation codes yet'}
            body={
              filtered
                ? 'Try another status or shop.'
                : tenant
                  ? 'Generate a code to let this shop extend its subscription from the app.'
                  : 'Codes are generated from a shop’s page and appear here.'
            }
            action={
              tenant && manage && !filtered
                ? { label: 'New code', onClick: () => setCreating(true) }
                : undefined
            }
          />
        }
      />

      {tenant && (
        <>
          <CreateCodeDialog
            open={creating}
            tenantName={tenant.name}
            onClose={() => setCreating(false)}
            onCreate={async (body) => {
              const code = await createCode(tenant.id, body);
              setCreating(false);
              setCreated(code);
              void refresh();
              void qc.invalidateQueries({ queryKey: tenantKeys.detail(tenant.id) });
            }}
          />
          <CodeRevealDialog
            code={created}
            tenantName={tenant.name}
            onClose={() => setCreated(null)}
          />
        </>
      )}
      <ReasonDialog
        open={revoking !== null}
        title="Revoke activation code?"
        body={
          <>
            The code ending <strong className="tabular">{revoking?.codeHint}</strong> stops working
            immediately. This can’t be undone.
          </>
        }
        confirmLabel="Revoke code"
        danger
        onClose={() => setRevoking(null)}
        onConfirm={async (reason) => {
          if (!revoking) return;
          await revokeCode(revoking.id, reason);
          notify.success('Code revoked');
          void refresh();
        }}
      />
      <DeliverDialog
        open={delivering !== null}
        hint={delivering?.codeHint ?? ''}
        onClose={() => setDelivering(null)}
        onDeliver={async (channel) => {
          if (!delivering) return;
          await deliverCode(delivering.id, channel);
          notify.success('Marked as delivered');
          void refresh();
        }}
      />
    </>
  );
}
